import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { foodPreferencesAPI } from '../utils/api';
import useUserStore from '../store/userStore';
import {
  addItem, applyDiff, countChanges, diffDraft, draftFromPayload, itemKey,
  mergeProfilePatch, removeItem, removeUnresolved, toSaveBody,
} from '../utils/foodPreferencesDraft';

// Settings food-preference state: explicit Save, optimistic concurrency.
//
//   status 'loading' -> editing is disabled, so a late load can never
//                       overwrite something the user just did
//   status 'ready'   -> draft vs saved baseline gives the dirty state
//   save()           -> PUT with expected_revision; on success the saved
//                       structure + legacy mirrors are merged into the store
//                       profile; on 409 the newer server state becomes the
//                       baseline and the user's pending edits are re-applied
//                       on top (still unsaved)
export default function useFoodPreferencesDraft() {
  var [status, setStatus] = useState('loading');
  var [baselinePayload, setBaselinePayload] = useState(null);
  var [draft, setDraft] = useState(null);
  var [saving, setSaving] = useState(false);
  var [conflictNotice, setConflictNotice] = useState(false);
  var [saveError, setSaveError] = useState(null);
  var mounted = useRef(true);

  var load = useCallback(function () {
    setStatus('loading');
    return foodPreferencesAPI.get()
      .then(function (resp) {
        if (!mounted.current) return;
        setBaselinePayload(resp.data);
        setDraft(draftFromPayload(resp.data));
        setStatus('ready');
      })
      .catch(function () {
        if (mounted.current) setStatus('error');
      });
  }, []);

  useEffect(function () {
    mounted.current = true;
    load();
    return function () { mounted.current = false; };
  }, [load]);

  var baseline = useMemo(function () {
    return baselinePayload ? draftFromPayload(baselinePayload) : null;
  }, [baselinePayload]);

  var diff = useMemo(function () {
    return baseline && draft ? diffDraft(baseline, draft) : null;
  }, [baseline, draft]);

  var changeCount = diff ? countChanges(diff) : 0;
  var dirty = changeCount > 0;

  var baselineKeys = useMemo(function () {
    var keys = {};
    if (baseline) {
      baseline.allergens.concat(baseline.dislikes).forEach(function (i) { keys[itemKey(i)] = true; });
    }
    return keys;
  }, [baseline]);

  var ready = status === 'ready';

  function add(list, item) {
    if (!ready) return;
    setSaveError(null);
    setDraft(function (d) { return addItem(d, list, item); });
  }
  function remove(list, key) {
    if (!ready) return;
    setDraft(function (d) { return removeItem(d, list, key); });
  }
  function undoRemove(list, item) {
    if (!ready) return;
    setDraft(function (d) { return addItem(d, list, item); });
  }
  function dropUnresolved(list, raw) {
    if (!ready) return;
    setDraft(function (d) { return removeUnresolved(d, list, raw); });
  }
  function discard() {
    if (baseline) setDraft(draftFromPayload(baselinePayload));
    setConflictNotice(false);
    setSaveError(null);
  }

  function save() {
    if (!ready || !draft || saving) return Promise.resolve(false);
    setSaving(true);
    setSaveError(null);
    var pending = diff;
    return foodPreferencesAPI.save(toSaveBody(draft, baselinePayload.revision))
      .then(function (resp) {
        var data = resp.data || {};
        if (!mounted.current) return true;
        setBaselinePayload(data.preferences);
        setDraft(draftFromPayload(data.preferences));
        setConflictNotice(false);
        var store = useUserStore.getState();
        store.setProfile(mergeProfilePatch(store.profile, data.profile_patch));
        return true;
      })
      .catch(function (err) {
        var resp = err && err.response;
        var detail = resp && resp.data && resp.data.detail;
        if (resp && resp.status === 409 && detail && detail.current && mounted.current) {
          setBaselinePayload(detail.current);
          setDraft(applyDiff(detail.current, pending));
          setConflictNotice(true);
        } else if (mounted.current) {
          setSaveError((detail && detail.message) || 'Couldn\'t save your food preferences. Please try again.');
        }
        return false;
      })
      .finally(function () {
        if (mounted.current) setSaving(false);
      });
  }

  return {
    status: status, ready: ready, draft: draft, baseline: baseline, baselineKeys: baselineKeys,
    diff: diff, dirty: dirty, changeCount: changeCount, saving: saving,
    conflictNotice: conflictNotice, saveError: saveError,
    load: load, add: add, remove: remove, undoRemove: undoRemove, dropUnresolved: dropUnresolved,
    discard: discard, save: save,
  };
}
